import { customFetch } from '@agos/api-client'
import type {
  GeocodeSuggestion,
  PlaceDetails as ServerPlaceDetails,
} from '@agos/api-client/model'
import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Address autocomplete, ported from legacy `hooks/use-address-autocomplete.ts`.
 *
 * Shared because three screens type an address into a field: the order builder,
 * the delivery depot setting, and the customer app. All three need the same
 * three guards, and all three get them wrong differently if each writes its own.
 *
 * The guards, in the order they matter:
 *
 *   1. **Debounce.** Every keystroke would otherwise be a request, and the
 *      provider is metered.
 *   2. **Abort.** A request for `Šur` is worthless once the user has typed
 *      `Šurany`, and cancelling it is what keeps the metered call count honest.
 *   3. **Staleness.** Abort is not instantaneous — a response already in flight
 *      can land after a newer query started. Every result is checked against the
 *      query that is current *now*, not the one that asked for it.
 *
 * The third is the one that bites: without it a slow response for an earlier
 * prefix overwrites the suggestions for what the user actually typed.
 */

const MIN_QUERY_LENGTH = 2
const DEBOUNCE_MS = 300

export interface AddressSuggestion {
  displayName: string
  placeId: string
  mainText?: string
  secondaryText?: string
}

export interface PlaceDetails {
  lat: number
  lon: number
  formattedAddress: string
}

/**
 * What the geocode endpoints answer - **the server's own types, not a copy of them** - §9.416.
 *
 * These were hand-declared here with the same four and three `snake_case` fields, optional. They
 * agreed with the server and were one rename away from not: `tsc` checks a read against whatever
 * type is named at the call, so `display_name` becoming `displayName` server-side would have moved
 * the generated type, left this one alone, and passed the build while the autocomplete rendered
 * blank suggestions. Address resolution feeds **delivery-zone matching**, and §10A.160's switch made
 * it the only lookup - so that failure is customer-facing and silent.
 *
 * **Derived with `Partial<>` rather than imported outright, and the tests below are what argued for
 * it.** `GeoDtos` annotates every field `RequiredMode.REQUIRED`, but two cases here assert that
 * `toSuggestion` copes with a payload carrying two of the four and with `{}` - so the tolerance is
 * deliberate, not an oversight, and importing the required type would have made those cases
 * uncompilable. `Partial<>` keeps them and still fails the build on a rename, because reading
 * `.display_name` off a `Partial<>` of a renamed field is an error either way.
 *
 * The local `PlaceDetails` in this file is the hook's **own** camelCase output, so the server's is
 * aliased rather than shadowing it.
 *
 * **The runtime guards below are kept deliberately.** A type is what the server promises; a guard is
 * for a server that breaks it, which on a passthrough of an upstream provider is not hypothetical.
 * The type turns a rename into a compile error; the guard turns a lie into an empty list.
 */
type RawSuggestion = Partial<GeocodeSuggestion>

type RawDetails = Partial<ServerPlaceDetails>

const NO_SUGGESTIONS: AddressSuggestion[] = []

export function useAddressAutocomplete(query: string) {
  const [fetched, setFetched] = useState<AddressSuggestion[]>([])
  const [isFetching, setIsFetching] = useState(false)
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inFlight = useRef<AbortController | null>(null)
  const currentQuery = useRef('')

  /**
   * Derived rather than cleared. A query too short to search has no suggestions
   * by definition, and writing that as state means an effect has to keep two
   * facts in step — which is where a stale list survives one render too long.
   */
  const isSearchable = query.length >= MIN_QUERY_LENGTH
  const suggestions = isSearchable ? fetched : NO_SUGGESTIONS
  const isLoading = isSearchable && isFetching

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current)
    if (inFlight.current) {
      inFlight.current.abort()
      inFlight.current = null
    }
    if (!isSearchable) return

    currentQuery.current = query

    debounce.current = setTimeout(() => {
      setIsFetching(true)
      const asked = query
      const controller = new AbortController()
      inFlight.current = controller

      void (async () => {
        try {
          const answer = await customFetch<RawSuggestion[]>(
            `/api/v1/geocode/search?q=${encodeURIComponent(asked)}`,
            { signal: controller.signal },
          )
          // The query moved on while this was in flight. Dropping the answer is
          // the difference between suggestions for what was typed and
          // suggestions for a prefix of it.
          if (currentQuery.current !== asked) return
          setFetched(Array.isArray(answer) ? answer.map(toSuggestion) : [])
        } catch {
          if (currentQuery.current === asked) setFetched([])
        } finally {
          if (currentQuery.current === asked) setIsFetching(false)
        }
      })()
    }, DEBOUNCE_MS)

    return () => {
      if (debounce.current) clearTimeout(debounce.current)
      if (inFlight.current) {
        inFlight.current.abort()
        inFlight.current = null
      }
    }
  }, [query, isSearchable])

  const clearSuggestions = useCallback(() => {
    setFetched([])
    setIsFetching(false)
  }, [])

  /**
   * The coordinates behind a chosen suggestion. Separate from the search because
   * the provider bills for it separately, and because only the one the user
   * picked is worth resolving.
   */
  const fetchPlaceDetails = useCallback(async (placeId: string): Promise<PlaceDetails | null> => {
    try {
      const details = await customFetch<RawDetails>(
        `/api/v1/geocode/details?place_id=${encodeURIComponent(placeId)}`,
      )
      if (details?.lat == null || details.lon == null) return null
      return {
        lat: details.lat,
        lon: details.lon,
        formattedAddress: details.formatted_address ?? '',
      }
    } catch {
      return null
    }
  }, [])

  return { suggestions, isLoading, clearSuggestions, fetchPlaceDetails }
}

export function toSuggestion(raw: RawSuggestion): AddressSuggestion {
  return {
    displayName: raw.display_name ?? '',
    placeId: raw.place_id ?? '',
    ...(raw.main_text ? { mainText: raw.main_text } : {}),
    ...(raw.secondary_text ? { secondaryText: raw.secondary_text } : {}),
  }
}
